import Phaser from 'phaser';
import { GAME_CONFIG } from '../config/game';

enum BossState {
  IDLE = 'idle',
  HIDING = 'hiding',
  PEEKING = 'peeking',
  ATTACKING = 'attacking',
  VULNERABLE = 'vulnerable',
  TRANSITIONING = 'transitioning',
  DEFEATED = 'defeated',
}

export class BlanketBoss extends Phaser.Physics.Arcade.Sprite {
  private projectiles: Phaser.Physics.Arcade.Group;
  private bossState: BossState = BossState.IDLE;
  private phase: number = 1;
  private maxHealth: number = 100; // Reduced for first boss (was 150)
  private health: number = 100;
  private battleStarted: boolean = false;
  private stateTimer: number = 0;
  private attackCooldown: number = 0;
  private vulnerableWindow: number = 2000; // Longer window for first boss (was 1200)
  private blanketSprite!: Phaser.GameObjects.Image;
  private emilySprite!: Phaser.GameObjects.Image;
  private attackIndicator?: Phaser.GameObjects.Text;
  private isDestroyed: boolean = false;
  
  // Projectile speed multiplier (lower = easier)
  private readonly SPEED_MULT = 0.8; // 20% slower projectiles

  /**
   * Check if this entity and its scene are still valid
   */
  private isValid(): boolean {
    return !this.isDestroyed && this.scene && this.active;
  }

  /**
   * Safe delayed call that checks validity before executing
   */
  private safeDelayedCall(delay: number, callback: () => void): void {
    this.scene.time.delayedCall(delay, () => {
      if (this.isValid()) {
        callback();
      }
    });
  }

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    projectiles: Phaser.Physics.Arcade.Group
  ) {
    super(scene, x, y, 'emily_boss');

    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.projectiles = projectiles;

    // Physics setup
    const body = this.body as Phaser.Physics.Arcade.Body;
    body.setCollideWorldBounds(true);
    body.setImmovable(true);
    body.setAllowGravity(false);

    // Visual setup - blanket covers Emily
    this.blanketSprite = scene.add.image(x, y, 'blanket');
    this.blanketSprite.setDepth(10);

    // Emily peeks out from blanket - use actual sprite if available
    const emilyTexture = scene.textures.exists('emily_sprite') ? 'emily_sprite' : 'emily_boss';
    this.emilySprite = scene.add.image(x, y - 30, emilyTexture);
    // Scale appropriately - the sprite is 32x48, we want it around 64-80 pixels tall
    this.emilySprite.setScale(emilyTexture === 'emily_sprite' ? 1.8 : 0.8);
    this.emilySprite.setVisible(false);
    this.emilySprite.setDepth(9);

    // Make main sprite invisible (using children)
    this.setVisible(false);

    // Blanket hover animation
    scene.tweens.add({
      targets: this.blanketSprite,
      y: y - 5,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  startBattle() {
    this.battleStarted = true;
    this.bossState = BossState.HIDING;
    this.stateTimer = 2000; // Start hiding for 2 seconds

    // Shake blanket to show Emily is hiding
    this.scene.tweens.add({
      targets: this.blanketSprite,
      x: this.x + 5,
      duration: 100,
      yoyo: true,
      repeat: 3,
    });
  }

  takeDamage(amount: number = 10) {
    if (this.bossState !== BossState.VULNERABLE) return;

    this.health -= amount;

    // Flash effect
    this.emilySprite.setTint(0xff0000);
    this.safeDelayedCall(100, () => {
      if (this.emilySprite && this.emilySprite.scene) {
        this.emilySprite.clearTint();
      }
    });

    // Phase transitions (3 phases for first boss - simpler)
    const healthPercent = this.health / this.maxHealth;
    if (healthPercent <= 0.33 && this.phase < 3) {
      this.phase = 3;
      this.transitionPhase();
    } else if (healthPercent <= 0.66 && this.phase < 2) {
      this.phase = 2;
      this.transitionPhase();
    }

    if (this.health <= 0) {
      this.defeated();
    }
  }

  private transitionPhase() {
    if (!this.isValid()) return;
    
    this.bossState = BossState.TRANSITIONING;
    this.stateTimer = 1500;

    // Dramatic shake
    this.scene.cameras.main.shake(500, 0.02);

    // Show phase text
    const phaseText = this.scene.add.text(
      GAME_CONFIG.width / 2,
      GAME_CONFIG.height / 2,
      `PHASE ${this.phase}`,
      {
        fontFamily: 'Georgia, serif',
        fontSize: '36px',
        color: '#ff4444',
        stroke: '#440000',
        strokeThickness: 4,
      }
    );
    phaseText.setOrigin(0.5, 0.5);

    this.scene.tweens.add({
      targets: phaseText,
      alpha: 0,
      scaleX: 2,
      scaleY: 2,
      duration: 1000,
      onComplete: () => {
        if (phaseText && phaseText.scene) phaseText.destroy();
      },
    });

    // Reduce vulnerable window in later phases (but still generous)
    switch (this.phase) {
      case 2: this.vulnerableWindow = 1800; break;
      case 3: this.vulnerableWindow = 1500; break;
    }
  }

  private defeated() {
    this.bossState = BossState.DEFEATED;

    // Victory animation
    this.scene.tweens.add({
      targets: [this.blanketSprite, this.emilySprite],
      alpha: 0.5,
      duration: 500,
    });

    // Blanket falls off
    this.scene.tweens.add({
      targets: this.blanketSprite,
      y: this.blanketSprite.y + 100,
      angle: 45,
      duration: 1000,
      ease: 'Bounce.easeOut',
    });

    // Emily emerges, happy
    this.emilySprite.setVisible(true);
    this.scene.tweens.add({
      targets: this.emilySprite,
      y: this.y - 60,
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 500,
    });
  }

  private attack() {
    // Show warning before attacking
    this.showAttackTelegraph(() => {
      // Only 3 phases for first boss
      switch (this.phase) {
        case 1: this.phase1Attack(); break;
        case 2: this.phase2Attack(); break;
        case 3: this.phase3Attack(); break;
      }
    });
  }
  
  private showAttackTelegraph(callback: () => void) {
    if (!this.isValid()) {
      callback();
      return;
    }
    
    // Show "!" warning above boss
    const warning = this.scene.add.text(this.x, this.y - 100, '❗', {
      fontSize: '28px',
    });
    warning.setOrigin(0.5);
    warning.setDepth(200);
    
    // Flash the blanket red
    this.blanketSprite.setTint(0xff6666);
    
    // Quick flash then attack
    this.scene.tweens.add({
      targets: warning,
      alpha: { from: 1, to: 0.4 },
      scaleX: 1.2,
      scaleY: 1.2,
      duration: 150,
      yoyo: true,
      repeat: 1,
      onComplete: () => {
        if (warning && warning.scene) warning.destroy();
        if (this.blanketSprite && this.blanketSprite.scene) {
          this.blanketSprite.clearTint();
        }
        callback();
      },
    });
  }

  // Phase 1: Basic pillows and waves
  private phase1Attack() {
    const attackChoice = Phaser.Math.Between(1, 2);
    if (attackChoice === 1) {
      this.pillowBarrage(3, false);
    } else {
      this.blanketWave();
    }
  }

  // Phase 2: Add stuffed animals and combos
  private phase2Attack() {
    const attackChoice = Phaser.Math.Between(1, 5);
    if (attackChoice === 1) {
      this.pillowBarrage(4, true);
    } else if (attackChoice === 2) {
      this.blanketWave();
      this.safeDelayedCall(500, () => this.pillowBarrage(2, false));
    } else if (attackChoice === 3) {
      this.summonStuffedAnimals(2);
    } else if (attackChoice === 4) {
      this.sleepyStars(3);
    } else {
      this.teddyBearCharge(); // Fun attack for phase 2
    }
  }

  // Phase 3: Final phase - slightly more intense but still fair
  private phase3Attack() {
    const attackChoice = Phaser.Math.Between(1, 6);
    if (attackChoice === 1) {
      this.pillowBarrage(4, true); // More pillows, some parryable
    } else if (attackChoice === 2) {
      this.blanketWave();
      this.safeDelayedCall(600, () => this.pillowBarrage(2, false));
    } else if (attackChoice === 3) {
      this.summonStuffedAnimals(2);
      this.safeDelayedCall(500, () => this.sleepyStars(2));
    } else if (attackChoice === 4) {
      this.flyingBooks(2);
    } else if (attackChoice === 5) {
      this.blanketTornado(); // Epic attack for final phase
    } else {
      this.blanketSlam(); // Dramatic slam attack
    }
  }

  private pillowBarrage(count: number, includeParryable: boolean) {
    for (let i = 0; i < count; i++) {
      this.safeDelayedCall(i * 200, () => {
        if (!this.isValid() || !this.projectiles || !this.scene) return;
        
        const isParryable = includeParryable && Phaser.Math.Between(1, 3) === 1;
        const pillow = this.projectiles.create(
          this.x,
          this.y - 20,
          isParryable ? 'pillow_pink' : 'pillow'
        ) as Phaser.Physics.Arcade.Image;

        if (!pillow || !pillow.active) return;

        pillow.setData('parryable', isParryable);

        // Arc trajectory toward player area
        const targetX = Phaser.Math.Between(50, GAME_CONFIG.width - 50);
        const angle = Phaser.Math.Angle.Between(this.x, this.y, targetX, GAME_CONFIG.height);
        const speed = (200 + this.phase * 40) * this.SPEED_MULT; // Slower for first boss

        const body = pillow.body as Phaser.Physics.Arcade.Body;
        body.setVelocity(
          Math.cos(angle) * speed,
          Math.sin(angle) * speed
        );
        body.setGravityY(300);

        // Spin (only if scene is valid)
        if (this.isValid() && this.scene) {
          this.scene.tweens.add({
            targets: pillow,
            angle: 360,
            duration: 500,
            repeat: -1,
          });
        }

        // Destroy after 5 seconds
        this.safeDelayedCall(5000, () => {
          if (pillow && pillow.active) pillow.destroy();
        });
      });
    }
  }

  private blanketWave() {
    if (!this.isValid()) return;
    
    // Horizontal wave across ground
    const wave = this.scene.add.graphics();
    wave.fillStyle(0xe879f9, 0.7);
    wave.fillRect(0, 0, GAME_CONFIG.width, 40);
    wave.setPosition(0, GAME_CONFIG.height - 80);
    wave.setAlpha(0);

    // Warning telegraph
    this.scene.tweens.add({
      targets: wave,
      alpha: 0.5,
      duration: 500,
      yoyo: true,
      onComplete: () => {
        if (!this.isValid() || !this.projectiles) {
          if (wave && wave.scene) wave.destroy();
          return;
        }
        
        // Create actual hitbox
        const waveHitbox = this.projectiles.create(
          GAME_CONFIG.width / 2,
          GAME_CONFIG.height - 60,
          'pillow'
        ) as Phaser.Physics.Arcade.Image;

        if (!waveHitbox) {
          if (wave && wave.scene) wave.destroy();
          return;
        }

        waveHitbox.setScale(GAME_CONFIG.width / 24, 1.5);
        waveHitbox.setTint(0xe879f9);
        waveHitbox.setAlpha(0.8);
        waveHitbox.setData('parryable', false);

        const body = waveHitbox.body as Phaser.Physics.Arcade.Body;
        body.setAllowGravity(false);

        this.safeDelayedCall(300, () => {
          if (waveHitbox && waveHitbox.active) waveHitbox.destroy();
          if (wave && wave.scene) wave.destroy();
        });
      },
    });
  }

  private summonStuffedAnimals(count: number) {
    for (let i = 0; i < count; i++) {
      this.safeDelayedCall(i * 400, () => {
        if (!this.isValid() || !this.projectiles || !this.scene) return;
        
        const startX = Phaser.Math.Between(100, GAME_CONFIG.width - 100);
        const animal = this.projectiles.create(
          startX,
          0,
          'stuffed_animal'
        ) as Phaser.Physics.Arcade.Image;

        if (!animal || !animal.active) return;

        animal.setData('parryable', false);

        const body = animal.body as Phaser.Physics.Arcade.Body;
        body.setVelocityY((150 + this.phase * 30) * this.SPEED_MULT);
        body.setBounce(0.6);
        body.setGravityY(300);

        // Bounce horizontally too
        body.setVelocityX(Phaser.Math.Between(-100, 100));

        // Destroy after 6 seconds
        this.safeDelayedCall(6000, () => {
          if (animal && animal.active) animal.destroy();
        });
      });
    }
  }

  // NEW ATTACK: Sleepy stars that float down and slow player
  private sleepyStars(count: number) {
    for (let i = 0; i < count; i++) {
      this.safeDelayedCall(i * 300, () => {
        if (!this.isValid() || !this.projectiles || !this.scene) return;
        
        const startX = Phaser.Math.Between(50, GAME_CONFIG.width - 50);
        const star = this.projectiles.create(startX, 50, 'pillow') as Phaser.Physics.Arcade.Image;
        
        if (!star || !star.active) return;
        
        // Make it look like a star
        star.setTint(0xffff00);
        star.setScale(0.5);
        star.setData('parryable', true); // Can parry stars!
        
        const body = star.body as Phaser.Physics.Arcade.Body;
        body.setVelocityY(80 + this.phase * 20);
        body.setAllowGravity(false);
        
        // Zigzag motion (only if scene is valid)
        if (this.isValid() && this.scene) {
          this.scene.tweens.add({
            targets: star,
            x: startX + Phaser.Math.Between(-100, 100),
            duration: 1000,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
          
          // Sparkle rotation
          this.scene.tweens.add({
            targets: star,
            angle: 360,
            duration: 800,
            repeat: -1,
          });
        }
        
        this.safeDelayedCall(6000, () => {
          if (star && star.active) star.destroy();
        });
      });
    }
  }

  // NEW ATTACK: Flying storybooks
  private flyingBooks(count: number) {
    for (let i = 0; i < count; i++) {
      this.safeDelayedCall(i * 400, () => {
        if (!this.isValid() || !this.projectiles || !this.scene) return;
        
        const side = Phaser.Math.Between(0, 1);
        const startX = side === 0 ? -30 : GAME_CONFIG.width + 30;
        const startY = Phaser.Math.Between(200, 500);
        
        const book = this.projectiles.create(startX, startY, 'pillow') as Phaser.Physics.Arcade.Image;
        
        if (!book || !book.active) return;
        
        book.setTint(0x8b4513); // Brown for book
        book.setScale(0.8, 0.6);
        book.setData('parryable', false);
        
        const body = book.body as Phaser.Physics.Arcade.Body;
        const bookSpeed = (160 + this.phase * 25) * this.SPEED_MULT;
        body.setVelocityX(side === 0 ? bookSpeed : -bookSpeed);
        body.setAllowGravity(false);
        
        // Flapping motion (only if scene is valid)
        if (this.isValid() && this.scene) {
          this.scene.tweens.add({
            targets: book,
            y: startY - 30,
            duration: 300,
            yoyo: true,
            repeat: -1,
            ease: 'Sine.easeInOut',
          });
        }
        
        this.safeDelayedCall(5000, () => {
          if (book && book.active) book.destroy();
        });
      });
    }
  }

  // NEW ATTACK: Blanket tornado that spins across screen
  private blanketTornado() {
    if (!this.isValid() || !this.projectiles) return;
    
    const tornado = this.scene.add.graphics();
    tornado.setDepth(50);
    
    const startX = Phaser.Math.Between(0, 1) === 0 ? -50 : GAME_CONFIG.width + 50;
    const direction = startX < 0 ? 1 : -1;
    
    // Draw tornado shape
    const drawTornado = () => {
      tornado.clear();
      tornado.fillStyle(0xe879f9, 0.7);
      for (let i = 0; i < 5; i++) {
        const y = 400 + i * 40;
        const width = 30 + i * 15;
        tornado.fillEllipse(0, y - 400, width, 20);
      }
    };
    drawTornado();
    tornado.setPosition(startX, 400);
    
    // Create hitbox
    const hitbox = this.projectiles.create(startX, 500, 'pillow') as Phaser.Physics.Arcade.Image;
    if (!hitbox || !hitbox.active) {
      if (tornado && tornado.scene) tornado.destroy();
      return;
    }
    
    hitbox.setAlpha(0);
    hitbox.setScale(2, 4);
    hitbox.setData('parryable', false);
    
    const hitboxBody = hitbox.body as Phaser.Physics.Arcade.Body;
    hitboxBody.setAllowGravity(false);
    
    // Move across screen
    this.scene.tweens.add({
      targets: [tornado, hitbox],
      x: direction > 0 ? GAME_CONFIG.width + 100 : -100,
      duration: 3000,
      ease: 'Linear',
      onUpdate: () => {
        // Spin effect
        if (tornado && tornado.scene) tornado.rotation += 0.2;
      },
      onComplete: () => {
        if (!this.isValid()) return;
        if (tornado && tornado.scene) tornado.destroy();
        if (hitbox && hitbox.active) hitbox.destroy();
      },
    });
  }

  // NEW ATTACK: Teddy bear charges across the screen
  private teddyBearCharge() {
    if (!this.isValid()) return;
    
    const side = Phaser.Math.Between(0, 1);
    const startX = side === 0 ? -60 : GAME_CONFIG.width + 60;
    const direction = side === 0 ? 1 : -1;
    
    // Warning indicator
    const warning = this.scene.add.text(
      GAME_CONFIG.width / 2,
      GAME_CONFIG.height - 150,
      '🧸 TEDDY CHARGE! 🧸',
      { fontSize: '18px', color: '#ff4444', stroke: '#000', strokeThickness: 3 }
    );
    warning.setOrigin(0.5);
    warning.setDepth(100);
    
    this.scene.tweens.add({
      targets: warning,
      alpha: 0,
      duration: 800,
      onComplete: () => {
        if (!this.isValid()) return;
        if (warning && warning.scene) warning.destroy();
      },
    });
    
    // Spawn teddy after warning
    this.safeDelayedCall(600, () => {
      if (!this.isValid() || !this.projectiles || !this.scene) return;
      
      const teddy = this.projectiles.create(startX, GAME_CONFIG.height - 100, 'stuffed_animal') as Phaser.Physics.Arcade.Image;
      
      if (!teddy || !teddy.active) return;
      
      teddy.setScale(2.5);
      teddy.setTint(0x8b4513);
      teddy.setData('parryable', false);
      teddy.setFlipX(direction < 0);
      
      const body = teddy.body as Phaser.Physics.Arcade.Body;
      body.setVelocityX(direction * (280 + this.phase * 30) * this.SPEED_MULT);
      body.setAllowGravity(false);
      body.setSize(60, 80);
      
      this.safeDelayedCall(4000, () => {
        if (teddy && teddy.active) teddy.destroy();
      });
    });
  }

  // NEW ATTACK: Blanket slam creates shockwave
  private blanketSlam() {
    if (!this.isValid() || !this.blanketSprite || !this.blanketSprite.scene) return;
    
    // Emily raises blanket
    this.scene.tweens.add({
      targets: this.blanketSprite,
      y: this.y - 80,
      scaleY: 1.3,
      duration: 400,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (!this.isValid() || !this.blanketSprite || !this.blanketSprite.scene) return;
        
        // SLAM DOWN
        this.scene.tweens.add({
          targets: this.blanketSprite,
          y: this.y + 20,
          scaleY: 0.7,
          duration: 150,
          ease: 'Bounce.easeOut',
          onComplete: () => {
            if (!this.isValid()) return;
            
            // Screen shake
            this.scene.cameras.main.shake(200, 0.03);
            
            // Create shockwaves going left and right
            this.createShockwave(this.x, -1);
            this.createShockwave(this.x, 1);
            
            // Reset blanket
            if (this.blanketSprite && this.blanketSprite.scene) {
              this.scene.tweens.add({
                targets: this.blanketSprite,
                y: this.y,
                scaleY: 1,
                duration: 500,
              });
            }
          },
        });
      },
    });
  }

  private createShockwave(startX: number, direction: number) {
    if (!this.isValid() || !this.projectiles || !this.scene) return;
    
    const wave = this.projectiles.create(startX, GAME_CONFIG.height - 60, 'pillow') as Phaser.Physics.Arcade.Image;
    
    if (!wave || !wave.active) return;
    
    wave.setTint(0xff69b4);
    wave.setScale(1.5, 0.5);
    wave.setAlpha(0.8);
    wave.setData('parryable', false);
    
    const body = wave.body as Phaser.Physics.Arcade.Body;
    body.setVelocityX(direction * (240 + this.phase * 40) * this.SPEED_MULT);
    body.setAllowGravity(false);
    
    // Expand as it travels (only if scene is valid)
    if (this.isValid() && this.scene) {
      this.scene.tweens.add({
        targets: wave,
        scaleX: 2.5,
        alpha: 0,
        duration: 1500,
        onComplete: () => {
          if (!this.isValid()) return;
          if (wave && wave.active) wave.destroy();
        },
      });
    }
  }
  getHealthPercent(): number {
    return this.health / this.maxHealth;
  }

  isDefeated(): boolean {
    return this.bossState === BossState.DEFEATED;
  }

  isVulnerable(): boolean {
    return this.bossState === BossState.VULNERABLE || this.bossState === BossState.PEEKING;
  }

  private showAttackIndicator() {
    // Create larger, more visible attack indicator
    this.attackIndicator = this.scene.add.text(this.x, this.y - 110, '⚔️ ATTACK NOW! ⚔️', {
      fontFamily: 'Georgia, serif',
      fontSize: '20px',
      color: '#00ff00',
      stroke: '#000000',
      strokeThickness: 4,
      backgroundColor: '#000000aa',
      padding: { x: 8, y: 4 },
    });
    this.attackIndicator.setOrigin(0.5, 0.5);
    this.attackIndicator.setDepth(200);

    // Pulsing green glow around boss
    const glow = this.scene.add.graphics();
    glow.lineStyle(4, 0x00ff00, 0.8);
    glow.strokeCircle(this.x, this.y, 80);
    glow.setDepth(15);
    (this.attackIndicator as any).glowEffect = glow;

    // Bounce animation
    this.scene.tweens.add({
      targets: this.attackIndicator,
      y: this.y - 120,
      scaleX: 1.1,
      scaleY: 1.1,
      duration: 250,
      yoyo: true,
      repeat: -1,
    });
    
    // Pulse the glow
    this.scene.tweens.add({
      targets: glow,
      alpha: 0.3,
      duration: 300,
      yoyo: true,
      repeat: -1,
    });
  }

  private hideAttackIndicator() {
    if (this.attackIndicator) {
      // Also destroy the glow effect if it exists
      const glow = (this.attackIndicator as any).glowEffect as Phaser.GameObjects.Graphics;
      if (glow && glow.scene) {
        glow.destroy();
      }
      this.attackIndicator.destroy();
      this.attackIndicator = undefined;
    }
  }

  // Clean up when destroyed
  override destroy(fromScene?: boolean) {
    this.isDestroyed = true;
    this.hideAttackIndicator();
    super.destroy(fromScene);
  }

  update(time: number, delta: number) {
    if (!this.battleStarted || this.bossState === BossState.DEFEATED || this.isDestroyed) return;

    this.stateTimer -= delta;
    this.attackCooldown -= delta;

    switch (this.bossState) {
      case BossState.HIDING:
        if (this.stateTimer <= 0) {
          this.bossState = BossState.PEEKING;
          this.stateTimer = this.vulnerableWindow;
          this.emilySprite.setVisible(true);

          // Peek animation
          this.scene.tweens.add({
            targets: this.emilySprite,
            y: this.y - 50,
            duration: 200,
          });
        }
        break;

      case BossState.PEEKING:
        // Player can attack during peek!
        if (this.stateTimer <= 0) {
          this.bossState = BossState.ATTACKING;
          this.stateTimer = 2500 - this.phase * 300; // Longer attack window in phase 1
        }
        break;

      case BossState.ATTACKING:
        if (this.attackCooldown <= 0) {
          this.attack();
          this.attackCooldown = 1500 - this.phase * 150; // More time between attacks
        }

        if (this.stateTimer <= 0) {
          this.bossState = BossState.VULNERABLE;
          this.stateTimer = this.vulnerableWindow;

          // Emily fully visible, vulnerable
          this.scene.tweens.add({
            targets: this.emilySprite,
            y: this.y - 70,
            duration: 200,
          });
        }
        break;

      case BossState.VULNERABLE:
        // Flashing to indicate vulnerability
        this.emilySprite.setAlpha(0.5 + 0.5 * Math.sin(time * 0.01));
        
        // Show "ATTACK!" indicator if not already showing
        if (!this.attackIndicator) {
          this.showAttackIndicator();
        }

        if (this.stateTimer <= 0) {
          this.hideAttackIndicator();
          this.bossState = BossState.HIDING;
          this.stateTimer = 1500 - this.phase * 200; // Shorter hide time = more vulnerable windows
          this.emilySprite.setAlpha(1);
          this.emilySprite.setVisible(false);

          // Hide animation
          this.scene.tweens.add({
            targets: this.emilySprite,
            y: this.y - 30,
            duration: 200,
          });
        }
        break;

      case BossState.TRANSITIONING:
        if (this.stateTimer <= 0) {
          this.bossState = BossState.HIDING;
          this.stateTimer = 1000;
          this.emilySprite.setVisible(false);
        }
        break;
    }
  }
}

